import { Dispatch, SetStateAction, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
// material
import { Link, Stack, Alert, Checkbox, TextField, IconButton, InputAdornment, FormControlLabel, Button, Box } from '@material-ui/core';
import { LoadingButton } from '@material-ui/lab';
import { makeStyles } from '@material-ui/styles';
import { MIconButton } from '../../@material-extend';
// routes
import { PATH_AUTH } from '../../../routes/paths';
// hooks
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack5';
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
//
import { signInWithRedirect } from 'aws-amplify/auth';
import { INITIAL_PATH } from '../../../constants';
import { AVAILABLE_MFA_METHODS } from 'src/@types/authentication';
import { SignInResult } from 'src/contexts/AwsCognitoContext';

type InitialValues = {
  email: string;
  password: string;
  remember: boolean;
  afterSubmit?: string;
};

// MFA対応 start --------------
type LoginFormProps = {
  setIsVerifyMfaCode: Dispatch<SetStateAction<boolean>>;
  setIsNeedNewPassword: Dispatch<SetStateAction<boolean>>;
  setActiveMfaMethod: Dispatch<SetStateAction<AVAILABLE_MFA_METHODS | null>>;
  setIsRequiredMfa: Dispatch<SetStateAction<boolean>>;
};
// MFA対応 end --------------

const useStyles = makeStyles({
  root: {
    background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
    border: 0,
    borderRadius: 8,
    boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
    color: 'white',
    height: 48,
    padding: '0 30px',
  },
});

// ----------------------------------------------------------------------

export default function LoginForm({ setIsVerifyMfaCode, setIsNeedNewPassword, setActiveMfaMethod, setIsRequiredMfa }: LoginFormProps) {
  const { login } = useAuth();
  const { t } = useTranslation();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const loginPathNameLocalStrageKey: string = 'callbackLoginPathName';

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('validation.mismatchFormat', { item: t('login.email') }))
      .required(t('validation.required-input', { item: t('login.email') })),
    password: Yup.string().required(t('validation.required-input', { item: t('login.password') })),
  });

  const formik = useFormik<InitialValues>({
    initialValues: {
      email: '',
      password: '',
      remember: true,
    },
    validationSchema: LoginSchema,
    onSubmit: async (values, { setErrors, setSubmitting, resetForm }) => {
      try {
        localStorage.removeItem(loginPathNameLocalStrageKey);

        // MFA対応 start --------------
        // ログイン
        const result: SignInResult = await login(values.email, values.password);
        console.log('result', result);

        // パスワード変更が必要な場合
        if (result.isRequiredNewPassword) {
          // パスワード入力画面表示
          setIsNeedNewPassword(true);
          setIsRequiredMfa(!!result.isRequiredMfa);
          return;
        }

        // MFA認証コード入力が必要な場合
        if (result.isRequiredMfa) {
          if (!result.mfaMethod) throw new Error("Cannot find User's Mfa Method.");
          // MFA認証コード入力画面を表示
          setIsVerifyMfaCode(true);
          setActiveMfaMethod(result.mfaMethod); // ユーザー選択済みのMFA方式
          return;
        }

        // MFA方式選択画面へ
        if (result.isRequiredMfaMethod) {
          navigate(PATH_AUTH.selectMfaMethod, { replace: true });
          return;
        }
        // MFA対応 end ----------------

        enqueueSnackbar(t('login.complete'), {
          variant: 'success',
          action: (key) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          ),
        });
        if (isMountedRef.current) {
          setSubmitting(false);

          window.location.pathname !== '/'
            ? navigate(window.location.pathname + window.location.search, { replace: true })
            : navigate(INITIAL_PATH, { replace: true });
        }
      } catch (err: any) {
        console.error(err);
        resetForm();
        if (isMountedRef.current) {
          setSubmitting(false);
          // MFA対応 start ----------------
          setErrors({ afterSubmit: err.message });
          // MFA対応 end ----------------
        }
      }
    },
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const classes = useStyles();

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 1 }}>
          <Button
            fullWidth
            color="secondary"
            size="large"
            variant="contained"
            onClick={() => {
              localStorage.setItem(loginPathNameLocalStrageKey, window.location.pathname + window.location.search);
              signInWithRedirect({ provider: { custom: 'mickey' } });
            }}>
            {t('login.commonLogin')}
          </Button>
        </Stack>
        <Stack sx={{ height: '80px', alignSelf: 'center' }}>
          <Box
            sx={{
              marginTop: '20px',
              alignSelf: 'center',
              fontSize: '20px',
              fontWeight: '10',
            }}>
            or
          </Box>
        </Stack>
        <Stack spacing={2}>
          {errors.afterSubmit && <Alert severity="error">{errors.afterSubmit}</Alert>}

          <TextField
            fullWidth
            size="medium"
            autoComplete="username"
            type="email"
            label={t('login.email')}
            {...getFieldProps('email')}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
          />

          <TextField
            fullWidth
            size="medium"
            autoComplete="current-password"
            type={showPassword ? 'text' : 'password'}
            label={t('login.password')}
            {...getFieldProps('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleShowPassword} edge="end">
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 0 }}>
          <Link component={RouterLink} variant="subtitle2" to={PATH_AUTH.resetPassword} sx={{ my: '9px' }}>
            {t('resetPassword.title')}
          </Link>
        </Stack>
        <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting} className={classes.root}>
        {t('login.marsPortalIdLogin')}
        </LoadingButton>
      </Form>
    </FormikProvider>
  );
}
